/**
 * 视频URL处理工具函数
 */

import { getVideoCDNPrefix, getVideoStoragePrefix } from '../config/video';

const FAST_TRANSLATE_PATH_SEGMENT = '/fast-translate/';

/**
 * 一键速译 COS 资源统一走同源 /media/fast-translate 代理（Nginx/Vite 反代腾讯 COS），
 * 避免 fetch 跨域及 mixed-content。支持相对路径与 COS 绝对 URL。
 */
export const resolveFastTranslateMediaUrl = (url: string | null | undefined): string | null => {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('/media/fast-translate')) {
    return trimmed;
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const { pathname } = new URL(trimmed);
      if (pathname.includes(FAST_TRANSLATE_PATH_SEGMENT)) {
        return `/media${pathname}`;
      }
    } catch {
      return null;
    }
    return null;
  }

  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  if (path.startsWith(FAST_TRANSLATE_PATH_SEGMENT) || path === '/fast-translate') {
    return `/media${path}`;
  }
  return null;
};

/**
 * 将导出/下载用的资源 URL 转为同源可 fetch 的地址。
 * 接口可能返回 CDN 完整 URL；浏览器 fetch 跨域会被 CORS 拦截，需走 /media、/storage 反代。
 */
export function toProxiedDownloadUrl(
  url: string | null | undefined,
  useMediaCache?: boolean
): string {
  const trimmed = String(url || "").trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("/media/") || trimmed.startsWith("/storage/")) {
    return trimmed;
  }

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const parsed = new URL(trimmed);
      const host = parsed.hostname.toLowerCase();
      const isKnownStorage = host.includes("volces.com") || host.includes("myqcloud.com");
      if (isKnownStorage) {
        const path = parsed.pathname.startsWith("/") ? parsed.pathname : `/${parsed.pathname}`;
        const suffix = parsed.search || "";
        if (path.startsWith("/fast-translate/")) {
          return `${getVideoCDNPrefix()}${path}${suffix}`;
        }
        const prefix = useMediaCache ? getVideoCDNPrefix() : getVideoStoragePrefix();
        return `${prefix}${path}${suffix}`;
      }
    } catch {
      /* ignore */
    }
    return trimmed;
  }

  return processVideoUrl(trimmed, useMediaCache);
}

/**
 * 处理视频URL，为相对路径添加代理前缀
 * @param url 原始URL
 * @param useCache 是否使用缓存路径（默认 false，使用 /storage）
 *                 true: 使用 /media（内容哈希资源，可强缓存）
 *                 false: 使用 /storage（非缓存资源）
 * @returns 处理后的完整URL
 */
export const processVideoUrl = (url: string | null | undefined, useCache: boolean = false): string => {
  if (!url) {
    return '';
  }

  const fastTranslateUrl = resolveFastTranslateMediaUrl(url);
  if (fastTranslateUrl) {
    return fastTranslateUrl;
  }

  // 绝对 URL：HTTPS 页面转同源 /media|/storage 代理，避免 fetch CORS / mixed-content
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return rewriteAbsoluteMediaUrlForPage(
      url,
      useCache ? getVideoCDNPrefix() : getVideoStoragePrefix()
    );
  }

  // ✅ 新增：如果URL已经包含代理前缀（/storage 或 /media），直接返回，避免重复拼接
  if (url.startsWith('/storage/') || url.startsWith('/media/')) {
    return url;
  }

  const prefix = useCache ? getVideoCDNPrefix() : getVideoStoragePrefix();
  
  // 如果前缀是相对路径（如 /media 或 /storage），需要确保路径拼接正确
  if (prefix.startsWith('/')) {
    // 前缀是相对路径，确保 url 以 / 开头
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `${prefix}${cleanUrl}`;
  } else {
    // 前缀是绝对路径（兼容旧配置），去掉 url 开头的 /，然后拼接
    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    return `${prefix}/${cleanUrl}`;
  }
};

/**
 * 处理目标台词音频URL（trans_audio）
 * 目标台词音频使用内容哈希命名，可以使用强缓存
 * @param url 原始URL
 * @returns 处理后的完整URL（使用 /media 路径）
 */
export const processTransAudioUrl = (url: string | null | undefined): string => {
  return processVideoUrl(url, true); // 使用缓存路径
};

/**
 * 获取视频播放URL，按优先级处理
 * 列表/弹窗等「外播」只使用 result_url、file_url，不使用 pure_video_url（编辑页 HLS 等另算）
 * @param item 视频数据项
 * @returns 处理后的视频URL
 */
export const getVideoPlayUrl = (item: any): string => {
  const rawUrl = item.result_url || item.file_url;

  if (!rawUrl) {
    return '';
  }

  // 精品译制 TOS 多为 middlestage/... 相对路径：须拼 /media 走 Nginx 反代。
  // 若直接塞进 <video src>，会被 SPA 当前路径（/hub/...）解析成 /hub/middlestage/... 导致 404。
  // 一键速译 COS 仍由 resolveFastTranslateMediaUrl → /media/fast-translate/... 处理。
  return processVideoUrl(rawUrl, true);
};

const M3U8_URL_RE = /\.m3u8(\?|#|$)/i;

/**
 * 预检：尽量等视频可进入 canplay 后再打开外播弹窗，避免先出现空壳。
 * HLS（.m3u8）在部分浏览器中无法用原生 video 预拉，直接 resolve，弹窗会立即打开。
 */
export const waitVideoCanPlay = (url: string, timeoutMs = 60000): Promise<void> => {
  const trimmed = (url || '').trim();
  if (!trimmed) {
    return Promise.reject(new Error('empty video url'));
  }
  if (M3U8_URL_RE.test(trimmed)) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', 'true');
    v.preload = 'auto';
    let settled = false;
    const cleanup = () => {
      v.pause();
      v.removeAttribute('src');
      v.load();
      v.remove();
    };
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      if (ok) {
        resolve();
      } else {
        reject(new Error('video canplay wait failed'));
      }
    };
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    v.addEventListener('canplay', () => finish(true), { once: true });
    v.addEventListener('error', () => finish(false), { once: true });
    v.src = trimmed;
  });
};

/**
 * 检查URL是否为有效的视频链接
 * @param url 视频URL
 * @returns 是否为有效链接
 */
export const isValidVideoUrl = (url: string): boolean => {
  return Boolean(url && url.length > 0 && (url.startsWith('http://') || url.startsWith('https://')));
};

const pageIsHttps = () =>
  typeof window !== "undefined" && window.location?.protocol === "https:";

const upgradeHttpToHttps = (url: string) =>
  url.startsWith("http://") ? `https://${url.slice("http://".length)}` : url;

/**
 * 封面、video、img 展示用地址。
 * 数字人资源在腾讯 COS（*.myqcloud.com），站点 /media、/storage 反代的是火山 TOS，
 * 改写成 /media/middlestage/... 会 404（见 player.jpg）。COS 地址必须直连。
 */
export const resolveDisplayMediaUrl = (raw?: string | null): string | null => {
  const cleaned = String(raw || "").trim().replace(/^["'`]+|["'`]+$/g, "");
  if (!cleaned) return null;

  if (cleaned.startsWith("http://") || cleaned.startsWith("https://")) {
    try {
      const host = new URL(cleaned).hostname.toLowerCase();
      if (host.includes("myqcloud.com")) {
        return pageIsHttps() ? upgradeHttpToHttps(cleaned) : cleaned;
      }
      if (host.includes("volces.com") && pageIsHttps() && cleaned.startsWith("http://")) {
        const parsed = new URL(cleaned);
        const path = parsed.pathname.startsWith("/") ? parsed.pathname : `/${parsed.pathname}`;
        return `${getVideoStoragePrefix()}${path}${parsed.search}`;
      }
    } catch {
      /* ignore */
    }
    if (cleaned.startsWith("https://")) return cleaned;
    return pageIsHttps() ? upgradeHttpToHttps(cleaned) : cleaned;
  }

  if (cleaned.startsWith("/media/") || cleaned.startsWith("/storage/")) return cleaned;
  const path = cleaned.startsWith("/") ? cleaned : `/${cleaned}`;
  return `${getVideoStoragePrefix()}${path}`;
};

/**
 * HTTPS 页面下将对象存储绝对 URL 转为同源 /media 路径，避免 fetch 触发 mixed-content。
 * 无法解析路径时，将 http 升级为 https。
 */
export const rewriteAbsoluteMediaUrlForPage = (
  url: string,
  cdnPrefix: string = getVideoCDNPrefix()
): string => {
  const trimmed = (url || '').trim();
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return trimmed;
  }

  const pageIsHttps =
    typeof window !== 'undefined' && window.location?.protocol === 'https:';

  if (pageIsHttps) {
    try {
      const { pathname } = new URL(trimmed);
      if (pathname && pathname !== '/') {
        return `${cdnPrefix}${pathname}`;
      }
    } catch {
      // ignore invalid URL
    }
    if (trimmed.startsWith('http://')) {
      return `https://${trimmed.slice('http://'.length)}`;
    }
  }

  return trimmed;
};
